// League List - Shows all leagues user belongs to
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLeague } from '../contexts/LeagueContext';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Input } from '../components';

const LeagueList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { leagues, loading, createLeague } = useLeague();

  // Auto-open create form if ?create=true is in URL
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowCreateForm(true);
    }
  }, [searchParams]);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueDescription, setNewLeagueDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreateLeague = async () => {
    if (!newLeagueName.trim()) {
      setError('League name is required');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const league = await createLeague(newLeagueName.trim(), newLeagueDescription.trim());
      setShowCreateForm(false);
      setNewLeagueName('');
      setNewLeagueDescription('');
      navigate(`/league/${league.id}`);
    } catch (err) {
      console.error('Error creating league:', err);
      setError('Failed to create league: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">Sign In Required</h2>
          <p className="text-gray-600 mb-4">Please sign in to view your leagues.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leagues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Leagues</h1>
            <p className="text-gray-600 mt-1">Manage your gaming groups</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/')}>
              Home
            </Button>
            <Button onClick={() => setShowCreateForm(true)}>
              + Create League
            </Button>
          </div>
        </div>

        {/* Create League Form */}
        {showCreateForm && (
          <Card className="mb-6">
            <h2 className="text-xl font-bold mb-4">Create New League</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  League Name *
                </label>
                <Input
                  type="text"
                  value={newLeagueName}
                  onChange={(e) => setNewLeagueName(e.target.value)}
                  placeholder="e.g., Friday Night Games"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={newLeagueDescription}
                  onChange={(e) => setNewLeagueDescription(e.target.value)}
                  placeholder="Describe your league..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  maxLength={200}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateLeague} disabled={creating}>
                  {creating ? 'Creating...' : 'Create League'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewLeagueName('');
                    setNewLeagueDescription('');
                    setError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* League List */}
        {leagues.length === 0 ? (
          <Card className="text-center py-12">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Leagues Yet</h2>
            <p className="text-gray-600 mb-6">
              Create a league to invite friends and organize competitions together.
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              Create Your First League
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {leagues.map(league => {
              const isAdmin = league.adminId === (user.odplayerId || user.oduserId);
              const memberCount = league.members?.length || 0;

              return (
                <Card
                  key={league.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/league/${league.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-bold text-gray-900">{league.name}</h3>
                        {isAdmin && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      {league.description && (
                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                          {league.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span>👥 {memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="text-4xl opacity-20">🏆</div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueList;
